import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

interface DeleteBlogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  blogTitle: string;
  isDeleting: boolean;
}

// Generate random 4-digit code
const generateCode = (): string => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

export const DeleteBlogDialog = ({
  open,
  onOpenChange,
  onConfirm,
  blogTitle,
  isDeleting,
}: DeleteBlogDialogProps) => {
  const [confirmCode, setConfirmCode] = useState('');
  const [userInput, setUserInput] = useState('');
  const [isValid, setIsValid] = useState(false);

  // Generate new code when dialog opens
  useEffect(() => {
    if (open) {
      setConfirmCode(generateCode());
      setUserInput('');
      setIsValid(false);
    }
  }, [open]);

  // Validate user input
  useEffect(() => {
    setIsValid(userInput === confirmCode);
  }, [userInput, confirmCode]);

  const handleConfirm = () => {
    if (isValid) {
      onConfirm();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-xl">Xóa Blog</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ⚠️ Cảnh báo: Hành động này không thể hoàn tác!
                </p>
                <p className="text-sm text-red-700">
                  Bạn đang chuẩn bị xóa vĩnh viễn blog <strong>"{blogTitle}"</strong> cùng với:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                  <li>Tất cả bài viết trong blog</li>
                  <li>Tất cả danh mục</li>
                  <li>Tất cả ảnh và dữ liệu liên quan</li>
                </ul>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  Để xác nhận, vui lòng nhập mã xác nhận bên dưới:
                </p>

                <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-600 mb-2">Mã xác nhận của bạn:</p>
                  <p className="text-3xl font-bold text-gray-900 tracking-widest font-mono">
                    {confirmCode}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-input" className="text-sm font-medium">
                    Nhập mã xác nhận
                  </Label>
                  <Input
                    id="confirm-input"
                    type="text"
                    placeholder="Nhập 4 chữ số"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength={4}
                    className={`text-center text-2xl tracking-widest font-mono ${
                      userInput.length === 4
                        ? isValid
                          ? 'border-green-500 bg-green-50'
                          : 'border-red-500 bg-red-50'
                        : ''
                    }`}
                    autoFocus
                    disabled={isDeleting}
                  />
                  {userInput.length === 4 && !isValid && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Mã xác nhận không đúng. Vui lòng thử lại.
                    </p>
                  )}
                  {isValid && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      ✓ Mã xác nhận chính xác
                    </p>
                  )}
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6">
          <AlertDialogCancel disabled={isDeleting}>Hủy bỏ</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!isValid || isDeleting}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {isDeleting ? 'Đang xóa...' : 'Xóa Blog'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
